<template>
    <FormulateForm class="search-filter" v-model="formData" @submit="search" autocomplete="off">
        <div ref="filterForm" class="filter-box">
            <div class="header">
                <div v-if="expandable" class="expand-filter" :class="{ 'is-expanded': expanded }">
                    <a class="button" size="slim" @click.prevent="expanded = !expanded">
                        <img
                            alt="Expandera sökfilter"
                            :class="{ rotate: expanded }"
                            src="@/assets/dropdown-arrow-white.svg"
                        />
                        Filter
                        <span class="filter-count">{{ valueCount }}</span>
                    </a>
                </div>
                <div class="header-filters" :style="{ alignItems: alignItems }">
                    <slot name="header-filters"></slot>
                </div>

                <Button
                    v-if="!expandable || headerButton"
                    class="submit"
                    type="submit"
                    variant="standard"
                    size="secondary"
                    :disabled="!props.enableSubmit && !isFormDataChanged"
                >
                    Filtrera
                </Button>

                <div class="quick-filters">
                    <slot name="quick-filters"></slot>
                </div>
            </div>
            <div v-show="expanded" class="main">
                <div class="fields">
                    <slot name="fields"></slot>
                </div>

                <div class="submit">
                    <Button
                        type="submit"
                        variant="standard"
                        size="secondary"
                        :disabled="!props.enableSubmit && !isFormDataChanged"
                    >
                        Filtrera
                    </Button>
                </div>
            </div>
        </div>
        <div class="tag-container" v-if="showTags">
            <span class="tag" v-for="{ key, label, value } in tags" :key="key">
                <span v-if="value === undefined">{{ label }}</span>
                <span v-else>{{ label }}: {{ value }}</span>
                <img src="@/assets/cross_error_grey.svg" alt="" @click="removeTag(key)" />
            </span>
            <div class="clear" v-if="tags.length">
                <Button @click="removeTags" variant="hollow" size="small">
                    <img src="@/assets/cross_circle.svg" alt="" /> Rensa
                </Button>
            </div>
        </div>
    </FormulateForm>
</template>

<script setup lang="ts">
    import Button from "@/components/Button.vue";
    import { omit } from "lodash";
    import { computed, PropType, ref, watch } from "vue";

    const emit = defineEmits(["input", "filter", "filterUpdated"]);

    const props = defineProps({
        value: {
            type: Object as PropType<Record<string, string>>,
            default: () => ({}),
        },
        fields: {
            type: Object as PropType<
                Record<string, { label: string; options?: { label: string; value: string }[]; isMultiple?: boolean }>
            >,
            default: () => ({}),
        },
        headerButton: {
            type: Boolean,
            default: false,
        },
        expandable: {
            type: Boolean,
            default: true,
        },
        isOpen: {
            required: false,
            type: Boolean as PropType<boolean>,
        },
        showTags: {
            required: false,
            type: Boolean as PropType<boolean>,
            default: true,
        },
        alignItems: {
            required: false,
            type: String as PropType<"normal" | "flex-start" | "center" | "flex-end" | "baseline" | "stretch">,
        },
        /**
         * Determines whether the submit button should be enabled.
         * If set to true, the submit button will be enabled unless the form data is unchanged.
         * Default is false, meaning the button is disabled by default.
         */
        enableSubmit: {
            type: Boolean,
            default: false,
        },
    });

    const filterForm = ref<HTMLDivElement>();
    const formData = ref<Record<string, string | { id: string; value: string; label: string }>>({});
    const expanded = ref<boolean>(props.isOpen ?? false);

    const tags = computed<{ key: string; label: string; value?: string }[]>(() => {
        let tags: { key: string; label: string; value?: string }[] = [];
        for (const [k, v] of Object.entries(formData.value)) {
            if (k in props.fields) {
                let f = props.fields[k];
                let value = `${v}`;
                let arr: { value: string; label: string }[] = [];
                /**
                 * If isMultiple=true, converts input values to their corresponding labels for multi-select fields.
                 * Transforms raw values into human-readable labels from predefined options.
                 *
                 */
                if (f.isMultiple && f.options) {
                    const splitValue = value.split(",").map((v) => v.trim());

                    arr = splitValue
                        .map((val) => f.options?.find((el) => el.value === val))
                        .filter((option): option is { value: string; label: string } => option !== undefined);

                    value = arr.map((k) => k.label).join(", ");
                } else if (f.options) {
                    let option = f.options.find((el) => el.value === value);

                    if (option !== undefined) {
                        value = `${option.label}`;
                    }
                }

                // Om fältet är en checkbox vill vi bara visa taggen om den är ikryssad - och då bara med labeln, utan värdet.
                // Det är viktigt att elementet heter samma sak som motsvarande nyckel i props.fields.
                const inputType = getInputTypeFromFilterFormElementName(k);
                if (inputType === "checkbox" && !f.isMultiple) {
                    if (value === "true") {
                        tags.push({ key: k, label: f.label });
                    }
                } else {
                    if (value === "true" || value === "false") {
                        value = value === "true" ? "Ja" : "Nej";
                    }

                    if (value !== "" && value !== "null" && value !== "undefined") {
                        tags.push({ key: k, label: f.label, value: value });
                    }
                }
            }
        }
        return tags;
    });

    const isFormDataChanged = computed(() => {
        return !Object.values(formData.value).every((x) => x === null || x === "");
    });

    // Emit a "filterUpdated" event with non-empty form data values when the form data changes
    watch(formData, (newValue) => {
        const updatedFilterValues = Object.fromEntries(
            Object.entries(newValue).filter(([_, v]) => v !== null && v !== "")
        );

        emit("filterUpdated", updatedFilterValues);
    });

    const valueCount = computed(() => {
        let count = 0;

        for (const [key, value] of Object.entries(formData.value)) {
            const inputType = getInputTypeFromFilterFormElementName(key);
            if (inputType === "checkbox" && !value) {
                continue;
            }

            /**
             * Increment count if `value` is not an empty string and not an empty array.
             */
            if (value !== "" && !(Array.isArray(value) && value.length === 0)) {
                count++;
            }
        }
        return count;
    });

    watch(
        () => props.value,
        (value) => {
            formData.value = value;
        },
        {
            immediate: true,
        }
    );

    function search(data: Record<string, string | { id: string; value: string; label: string }>) {
        formData.value = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== null && v !== ""));
        expanded.value = false;
        emitInput();
    }

    function removeTag(key: string) {
        formData.value = omit(formData.value, key);
        emitInput();
    }

    function removeTags() {
        formData.value = {};
        emitInput();
    }

    function emitInput() {
        emit("input", formData.value);
    }

    /**
     * Hämtar typen av input-elementet med namnet @param name
     * Elementnamnet ska motsvara namnet på nyckeln i props.fields och måste vara unikt
     */
    function getInputTypeFromFilterFormElementName(name: string) {
        const inputElements = filterForm?.value?.getElementsByTagName("input");
        const element: HTMLInputElement | undefined = Array.from(inputElements ?? []).find((e) => e.name === name);

        return element?.type ?? "";
    }
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/mixins/button";

    .search-filter {
        margin-bottom: 24px;

        .formulate-input[data-classification="text"][data-type="search"] {
            ::v-deep input[type="search"] {
                width: 100%;
            }
        }

        ::v-deep .search-field {
            margin: 0;
        }

        // TODO: Detta gör så att inputfälten inom komponenten blir smalare och texten mindre,
        // men bör nog på sikt lösas centralt så att inputfält kan ställas in som size="slim"
        // eller dylikt och göra det till standard i SearchFilter.
        ::v-deep input,
        ::v-deep .vs__dropdown-toggle,
        ::v-deep select,
        ::v-deep .vs__selected {
            max-height: 38px;
            line-height: 16px !important;
            font-size: 14px !important;
        }
        ::v-deep select {
            padding-top: 12px !important;
        }
        ::v-deep .formulate-input-label {
            font-size: 14px;
            line-height: 17px;
            margin-bottom: 4px;
        }
    }

    .filter-box {
        background-color: $color-background-puff;
        border-radius: 8px;
    }

    .header {
        padding: 15px 30px;
        border-bottom: 2px solid #ffffff;
        display: flex;
        align-items: center;
        gap: 20px;

        .header-filters {
            display: flex;
            align-items: flex-end;
            gap: 20px;
        }

        .quick-filters {
            flex-grow: 3;
        }
    }

    .expand-filter {
        display: flex;
        align-items: center;

        .button {
            @include button;
            @include button-standard;
            @include button-slim;

            .rotate {
                margin-right: 10px;
                transition: transform 0.3s ease-in-out;
            }

            .filter-count {
                background: #ffffff;
                height: 16px;
                width: 16px;
                margin-left: 8px;
                border-radius: 16px;
                color: $color-primary;
                font-family: Roboto Medium;
                font-size: 10px;
                line-height: 15px;
            }
        }

        &.is-expanded {
            .rotate {
                transform: rotate(180deg);
            }
        }
    }

    .quick-filters {
        display: flex;
        justify-content: flex-end;
        gap: 30px;
        flex-wrap: wrap;

        ::v-deep a {
            cursor: pointer;
            font-size: 14px;
            line-height: 16px;
            color: $color-primary-darken;
            display: flex;
            align-items: center;

            &:hover,
            &.active {
                border-bottom: 3px solid $color-primary-darken;
                margin-bottom: -3px;
                text-decoration: none;
            }
        }
    }

    .main {
        padding: 16px;
    }

    .fields {
        display: flex;
        flex-wrap: wrap;
        column-gap: 25px;
        margin-bottom: 20px;

        ::v-deep & > * {
            width: 256px;
        }

        /**
         * Align the checkbox with the rest of the fields.
         */
        ::v-deep(.formulate-input:has([data-type="checkbox"])) {
            display: flex;
            align-items: flex-end;
        }
    }

    .tag-container {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        margin: 12px 0;

        .tag {
            background-color: $color-background-puff;
            height: 32px;
            border-radius: 20px;
            color: $tabs-text-color;
            padding: 8px 12px 8px 14px;
            display: flex;
            align-items: center;
            gap: 12px;
            font-family: Roboto Medium, sans-serif;
            font-size: 14px;
            line-height: 19px;
        }

        .clear {
            display: flex;
            align-items: center;
        }
    }
</style>
